/**
 * Public API (customer-facing /v1) constants
 */
/**
 * Scopes grantable to Public API keys (stored on ApiKeyCredential.scopes)
 */
export var PublicApiScope;
(function (PublicApiScope) {
    PublicApiScope["INVOCATIONS_WRITE"] = "invocations:write";
    PublicApiScope["INVOCATIONS_READ"] = "invocations:read";
    PublicApiScope["CALLS_READ"] = "calls:read";
    PublicApiScope["AGENTS_WRITE"] = "agents:write";
    PublicApiScope["AGENTS_READ"] = "agents:read";
    PublicApiScope["TRIGGERS_WRITE"] = "triggers:write";
    PublicApiScope["TRIGGERS_READ"] = "triggers:read";
    PublicApiScope["WEBHOOKS_WRITE"] = "webhooks:write";
    PublicApiScope["WEBHOOKS_READ"] = "webhooks:read";
    PublicApiScope["COMPANY_READ"] = "company:read";
    // Internal-service scope (unified-api gateway) — granted explicitly, never by default
    PublicApiScope["INTEGRATIONS_HUBSPOT_PROXY"] = "integrations:hubspot:proxy";
    PublicApiScope["INTEGRATIONS_BUILDIUM_PROXY"] = "integrations:buildium:proxy";
})(PublicApiScope || (PublicApiScope = {}));
const INTEGRATION_PROXY_SCOPES = new Set([
    PublicApiScope.INTEGRATIONS_HUBSPOT_PROXY,
    PublicApiScope.INTEGRATIONS_BUILDIUM_PROXY,
]);
/**
 * Vendor identity for the unified-api gateway. Same string appears as the
 * `integration` field on every audit log line; kept lowercase for continuity
 * with the pre-refactor `'hubspot'` value that dashboards may key on.
 */
export var ProxyIntegrationVendor;
(function (ProxyIntegrationVendor) {
    ProxyIntegrationVendor["HUBSPOT"] = "hubspot";
    ProxyIntegrationVendor["BUILDIUM"] = "buildium";
})(ProxyIntegrationVendor || (ProxyIntegrationVendor = {}));
export const PROXY_SCOPE_FOR_VENDOR = {
    [ProxyIntegrationVendor.HUBSPOT]: PublicApiScope.INTEGRATIONS_HUBSPOT_PROXY,
    [ProxyIntegrationVendor.BUILDIUM]: PublicApiScope.INTEGRATIONS_BUILDIUM_PROXY,
};
export const VENDOR_FOR_PROXY_SCOPE = new Map([
    [PublicApiScope.INTEGRATIONS_HUBSPOT_PROXY, ProxyIntegrationVendor.HUBSPOT],
    [PublicApiScope.INTEGRATIONS_BUILDIUM_PROXY, ProxyIntegrationVendor.BUILDIUM],
]);
export function isProxyScope(scope) {
    return INTEGRATION_PROXY_SCOPES.has(scope);
}
// Default grant for new keys — internal-service scopes are excluded on purpose
export const ALL_PUBLIC_API_SCOPES = Object.values(PublicApiScope).filter(scope => !INTEGRATION_PROXY_SCOPES.has(scope));
/**
 * Scopes a tenant may grant to one of its own keys given the vendors it has
 * actively connected. Non-proxy scopes are always grantable; a proxy scope is
 * grantable only when the corresponding vendor is connected.
 */
export function grantableScopesFor(connectedVendors) {
    const proxy = connectedVendors.map(v => PROXY_SCOPE_FOR_VENDOR[v]);
    return [...ALL_PUBLIC_API_SCOPES, ...proxy];
}
/**
 * Key format: sk_super_{clientId}_{secret} — clientId enables O(1) lookup,
 * secret is scrypt-hashed at rest
 */
export const PUBLIC_API_KEY_PREFIX = 'sk_super_';
/**
 * Legacy encrypted-key prefix, retained for existing integrations
 */
export const LEGACY_API_KEY_PREFIX = 'sk_live_';
/**
 * Events deliverable to Public API webhook subscriptions
 */
export var PublicWebhookEvent;
(function (PublicWebhookEvent) {
    PublicWebhookEvent["CALL_PROCESSED"] = "call.processed";
    PublicWebhookEvent["SESSION_COMPLETED"] = "session.completed";
})(PublicWebhookEvent || (PublicWebhookEvent = {}));
export const ALL_PUBLIC_WEBHOOK_EVENTS = Object.values(PublicWebhookEvent);
/**
 * Default rate limits when PUBLIC_API_RPM_LIMIT / PUBLIC_API_BURST_LIMIT are unset
 */
export const PUBLIC_API_DEFAULT_RPM_LIMIT = 30;
export const PUBLIC_API_DEFAULT_BURST_LIMIT = 10;
//# sourceMappingURL=public-api.js.map